import React, { useState } from 'react';
import InfoModal from './InfoModal';

const GuoNei: React.FC<{ selectedProcessedFile: string | null }> = ({ selectedProcessedFile }) => {
  const [file, setFile] = useState<File | null>(null);
  const [extractedData, setExtractedData] = useState<any[]>([]);
  const [modalData, setModalData] = useState<any>(null);
  const [modalTitle, setModalTitle] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [keptData, setKeptData] = useState<any[]>([]);
  const [notKeptData, setNotKeptData] = useState<any[]>([]);
  const [showCsvData, setShowCsvData] = useState<'keep' | 'not_keep' | null>(null);
  const [filename, setFilename] = useState<string>('');
  const [taskId, setTaskId] = useState<string | null>(null);
  const [isDownloaded, setIsDownloaded] = useState<boolean>(false);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert('请选择一个文件。');
      return;
    }

    // Reset states
    setExtractedData([]);
    setGenerationStatus('');
    setShowCsvData(null);
    setKeptData([]);
    setNotKeptData([]);
    setFilename('');
    setTaskId(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/upload_guo_nei', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setExtractedData(result.data);
      setFilename(result.filename);
      alert('文件上传成功并已处理。');
    } catch (error: unknown) {
      console.error('File upload error:', error);
      if (error instanceof Error) {
        alert(`文件上传失败: ${error.message}`);
      } else {
        alert(`文件上传失败: ${String(error)}`);
      }
    }
  };

  async function handleGenerateComments() {
    if (!selectedProcessedFile) {
      alert('请在“自家网络设置”中选择一个已处理的网络文件。');
      return;
    }
    setGenerationStatus('正在生成评论...');
    setIsDownloaded(false);
    try {
      const response = await fetch('/generate_guo_nei_comments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          extracted_data: extractedData, 
          filename: filename,
          selected_network: selectedProcessedFile 
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setGenerationStatus(`评论生成已开始 (任务ID: ${data.task_id})...`);
      setTaskId(data.task_id);

      // Poll for task completion
      const intervalId = setInterval(async () => {
        try {
          const taskResponse = await fetch(`/tasks/${data.task_id}`);
          const taskData = await taskResponse.json();
          if (taskData.status === 'completed') {
            clearInterval(intervalId);
            setGenerationStatus('评论生成完成。');
          } else if (taskData.status === 'failed') {
            clearInterval(intervalId);
            setGenerationStatus(`评论生成失败: ${taskData.error}`);
          }
        } catch (error) {
          clearInterval(intervalId);
          setGenerationStatus('获取任务状态失败。');
          console.error("Task status error:", error);
        }
      }, 2000); // Poll every 2 seconds
    } catch (error: unknown) {
      console.error('Comment generation error:', error);
      if (error instanceof Error) {
        alert(`评论生成失败: ${error.message}`);
      } else {
        alert(`评论生成失败: ${String(error)}`);
      }
    }
  }

  const handleDownloadZip = async () => {
    if (!taskId) return;
    
    try {
      const response = await fetch(`/download_results/${taskId}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${taskId}_results.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setIsDownloaded(true);

    } catch (error: unknown) {
      console.error('Download error:', error);
      if (error instanceof Error) {
        alert(`下载失败: ${error.message}`);
      } else {
        alert(`下载失败: ${String(error)}`);
      }
    }
  };

  async function fetchCsvData(file: 'keep' | 'not_keep') {
    if (!taskId) {
        alert("无法获取CSV数据，因为任务ID不存在。");
        return;
    }
    const fileName = file === 'keep' ? `${taskId}_s7toKeep.result.csv` : `${taskId}_s7notToKeep.result.csv`;
    try {
      const response = await fetch(`/results/parsed-csv/${fileName}`); // ?v=${Date.now()}`);
      const data = await response.json();

      if (Array.isArray(data)) {
        if (file === 'keep') {
          setKeptData(data);
        } else {
          setNotKeptData(data);
        }
      } else {
        console.error("Fetched data is not an array:", data);
        if (file === 'keep') {
          setKeptData([]);
        } else {
          setNotKeptData([]);
        }
      }
      setShowCsvData(file);
    } catch (error) {
      console.error(`Failed to fetch ${fileName}:`, error);
    }
  }

  return (
    <div>
      <h2>国内网络信息</h2>
      <p>上传新网络信息</p>
      <input type="file" onChange={handleFileChange} />
      <button onClick={handleUpload}>上传</button>

      {extractedData.length > 0 && (
        <div>
          {generationStatus !== '评论生成完成。' ? (
            <button onClick={handleGenerateComments} style={{ marginTop: '10px' }} disabled={generationStatus.includes('正在')}>
              {generationStatus || '生成协调意见'}
            </button>
          ) : (
            !isDownloaded && (
              <div>
                <button onClick={handleDownloadZip}>下载协调意见</button>
                <button onClick={() => fetchCsvData('keep')}>包含的重叠</button>
                <button onClick={() => fetchCsvData('not_keep')}>舍弃的重叠</button>
              </div>
            )
          )}

          {showCsvData ? (
            <div>
              <h3>{showCsvData === 'keep' ? '包含的重叠' : '舍弃的重叠'}</h3>
              <button onClick={() => setShowCsvData(null)}>返回</button>
              <table className="data-table">
                <thead>
                  <tr>
                    {(showCsvData === 'keep' ? keptData : notKeptData).length > 0 &&
                      Object.keys(((showCsvData === 'keep' ? keptData : notKeptData)[0]) || {}).map((header: string) => <th key={header}>{header}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(showCsvData === 'keep' ? keptData : notKeptData).map((row: any, index: number) => (
                    <tr key={index}>
                      {Object.keys(row).map((key: any, i: number) => <td key={i}>{row[key]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div>
              <h3>提取的数据:</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ADM</th>
                    <th>Sat Name</th>
                    <th>Freq Min</th>
                    <th>Freq Max</th>
                    <th>Service</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedData.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item.adm}</td>
                      <td>{item.sat_name}</td>
                      <td>{item.freq_min}</td>
                      <td>{item.freq_max}</td>
                      <td>{item.service} ({item.isPrimary ? 'Primary' : 'Secondary'})</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <InfoModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        data={modalData}
      />
    </div>
  );
};

export default GuoNei;
